import React, { useState } from 'react';

const InstanceItem = ({ instance, onStart, onStop, loading, view = 'desktop', pollingStatus }) => {
  // Add state for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Determine if instance is being polled
  const isPolling = !!pollingStatus;
  
  // Get display state incorporating polling status
  const getDisplayState = () => {
    // If instance is being polled, show appropriate transitioning state
    if (isPolling) {
      if (pollingStatus === 'running') return 'starting...';
      if (pollingStatus === 'stopped') return 'stopping...';
    }
    return instance.state;
  };
  
  // Determine the state color
  const getStateColor = (state) => {
    switch (state) {
      case 'running':
        return 'bg-green-100 text-green-800';
      case 'stopped':
        return 'bg-red-100 text-red-800';
      case 'pending':
      case 'starting...':
        return 'bg-yellow-100 text-yellow-800';
      case 'stopping':
      case 'stopping...':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Determine if start/stop buttons should be disabled
  const canStart = instance.state === 'stopped' && !isPolling;
  const canStop = instance.state === 'running' && !isPolling;

  // Handle the stop button click
  const handleStopClick = () => {
    setShowConfirmation(true);
  };

  // Handle confirmation dialog confirm action
  const handleConfirmStop = () => {
    onStop(instance.id, instance.accountId);
    setShowConfirmation(false);
  };

  // Handle confirmation dialog cancel action
  const handleCancelStop = () => {
    setShowConfirmation(false);
  };

  // Spinner component for polling states
  const LoadingSpinner = () => (
    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  // Confirmation dialog component
  const ConfirmationDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Stop Instance</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to stop the instance "{instance.name}"? This will shut down the instance and any applications running on it.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancelStop}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmStop}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Stop Instance
          </button>
        </div>
      </div>
    </div>
  );

  // Desktop view
  if (view === 'desktop') {
    return (
      <>
        {showConfirmation && <ConfirmationDialog />}
        <div className="grid grid-cols-12 p-4 items-center">
          <div className="col-span-2 font-medium text-gray-900 truncate">
            {instance.name || <span className="text-gray-400 italic">No Name</span>}
          </div>
          
          <div className="col-span-2 text-sm text-gray-600 truncate">
            {instance.id}
          </div>
          
          <div className="col-span-1 text-sm text-gray-600">
            {instance.type}
          </div>
          
          <div className="col-span-1 pr-4 flex justify-center">
            <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center justify-center ${getStateColor(getDisplayState())}`}>
              {isPolling && <LoadingSpinner />}
              {getDisplayState()}
            </span>
          </div>
          
          <div className="col-span-1 text-sm text-gray-600 pl-2">
            {instance.accountName || 'Unknown Account'}
          </div>
          
          <div className="col-span-2 text-sm text-gray-600">
            {instance.publicIp ? instance.publicIp : '-'}
          </div>
          
          <div className="col-span-3 flex space-x-2">
            <button
              onClick={() => onStart(instance.id, instance.accountId)}
              disabled={!canStart || loading || isPolling}
              className={`px-3 py-1 text-sm rounded-md ${
                canStart && !loading && !isPolling
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              Start
            </button>
            
            <button
              onClick={handleStopClick}
              disabled={!canStop || loading || isPolling}
              className={`px-3 py-1 text-sm rounded-md ${
                canStop && !loading && !isPolling
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              Stop
            </button>
          </div>
        </div>
      </>
    );
  }

  // Mobile view
  return (
    <>
      {showConfirmation && <ConfirmationDialog />}
      <div className="flex flex-col space-y-3">
        <div className="flex justify-between items-center">
          <div className="font-medium text-gray-900 truncate max-w-[70%]">
            {instance.name || <span className="text-gray-400 italic">No Name</span>}
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center ${getStateColor(getDisplayState())}`}>
            {isPolling && <LoadingSpinner />}
            {getDisplayState()}
          </span>
        </div>
        
        <div className="flex flex-col space-y-2 text-sm text-gray-600">
          <div className="flex flex-col">
            <span className="font-semibold">ID:</span> 
            <span className="break-all">{instance.id}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-semibold">Type:</span> {instance.type}
            </div>
            <div>
              <span className="font-semibold">Account:</span> {instance.accountName || 'Unknown'}
            </div>
            {instance.publicIp && (
              <div className="col-span-2">
                <span className="font-semibold">Public IP:</span> {instance.publicIp}
              </div>
            )}
            {instance.privateIp && (
              <div className="col-span-2">
                <span className="font-semibold">Private IP:</span> {instance.privateIp}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex space-x-2 pt-2">
          <button
            onClick={() => onStart(instance.id, instance.accountId)}
            disabled={!canStart || loading || isPolling}
            className={`flex-1 px-3 py-2 text-sm rounded-md ${
              canStart && !loading && !isPolling
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            Start
          </button>
          
          <button
            onClick={handleStopClick}
            disabled={!canStop || loading || isPolling}
            className={`flex-1 px-3 py-2 text-sm rounded-md ${
              canStop && !loading && !isPolling
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            Stop
          </button>
        </div>
      </div>
    </>
  );
};

export default InstanceItem; 